<template>
    <Modal modal-name="pop-up-modal" modal-class="pop-up-modal" size="lg" :show-close-button="false" @close="show = false">
        <div class="pop-up-modal__body">
            <div
                v-if="$device.isDesktop && popUp.imageUrl"
                class="pop-up-modal__image-wrapper"
                :style="`background-image: url('${popUp.imageUrl}')`"
            ></div>
            <div class="pop-up-modal__main rounded" :style="popUp.imageUrl ? '' : 'width: 100%'">
                <div class="text-right">
                    <button class="pop-up-modal__close" @click="hideModal()">&times;</button>
                </div>
                <div v-if="popUp.title" class="pop-up-modal__title" v-html="popUp.title"></div>
                <div v-if="popUp.text" class="pop-up-modal__text" v-html="popUp.text"></div>
                <a
                    v-if="popUp.ctaText && popUp.ctaUrl"
                    :href="popUp.ctaUrl"
                    target="_blank"
                    class="pop-up-modal__primary-button"
                    @click="clickCta()"
                    v-html="popUp.ctaText"
                ></a>
            </div>
        </div>
    </Modal>
    <div v-if="!show">
        <button class="pop-up-modal__close-box d-flex" @click="showModal()">
            <div v-if="popUp.title" class="mt-1" v-html="popUp.title"></div>
            <div class="ico ico-open-modal"></div>
        </button>
    </div>
</template>

<script lang="ts" setup>
import PopUp from '~/models/StaticContent/PopUp'
import Modal from '~/components/Modal.vue'
import DataLayer from '~/extensions/DataLayer'
import { useCookie } from '#imports'

const props = defineProps({
    popUp: {
        type: Object as PropType<PopUp>,
        required: true,
    },
    showImmediately: {
        type: Boolean,
        default: false,
    },
})
const show = ref(false)
const alreadyTriggered = ref(false)

const clickCta = () => {
    DataLayer.push({
        event: 'pop_up',
        action: 'click',
        cta: props.popUp.ctaText,
        pop_up_name: props.popUp.title,
        page_url: location.href,
        landing_page: props.popUp.ctaUrl,
    })
}

const hideModal = () => {
    show.value = false
    setTimeout(() => {
        useNuxtApp().$bus.$emit('hideModal')
    })
}

const showModal = (eventOpenType = 'onClick') => {
    show.value = true
    setTimeout(() => {
        useNuxtApp().$bus.$emit('showModal', 'pop-up-modal')
        alreadyTriggered.value = true

        DataLayer.push({
            event: 'pop_up',
            action: 'open',
            type: eventOpenType,
            pop_up_name: props.popUp.title,
            page_url: location.href,
            landing_page: props.popUp.ctaText && props.popUp.ctaUrl ? props.popUp.ctaUrl : null,
        })
    })
}

const triggerOnScroll = () => {
    return document.documentElement.scrollTop > document.getElementsByTagName('body')[0].offsetHeight / 5
}

const leftViews = () => {
    const cookieName = 'popUp' + props.popUp.id
    const popUpData = useCookie<{ leftViews: number; expirationDate: string } | undefined>(cookieName).value

    if (popUpData === undefined) {
        const maxViews = 1
        const date = new Date()
        date.setHours(date.getHours() + 24)

        useCookie(cookieName, { expires: date }).value = JSON.stringify({
            leftViews: maxViews - 1,
            expirationDate: date,
        })

        return true
    } else if (popUpData.leftViews !== 0) {
        useCookie(cookieName, { expires: new Date(Date.parse(popUpData.expirationDate)) }).value = JSON.stringify({
            leftViews: popUpData.leftViews - 1,
            expirationDate: popUpData.expirationDate,
        })

        return true
    }

    return false
}

onMounted(() => {
    if (props.showImmediately && !alreadyTriggered.value && leftViews()) {
        showModal('onLoad')
    }

    window.onscroll = () => {
        if (!alreadyTriggered.value && triggerOnScroll() && leftViews()) {
            showModal('onScroll')
        }
    }

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            hideModal()
        }
    })
})
</script>

<style lang="scss">
.pop-up-modal__close-box {
    position: fixed;
    overflow: visible;
    opacity: 1;
    border: 0;
    z-index: 10;
    transition-duration: 250ms;
    transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
    transition-property: opacity, top, bottom;
    padding: 12px 14px 12px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    background: rgba(255, 244, 242, 1);
    color: #000;
    box-shadow: 0 0 11px 0 rgba(0, 0, 0, 0.17);
    cursor: pointer;
    bottom: 5%;
    left: 1%;

    @media (max-width: 768px) {
        left: 50%;
        bottom: 90px;
        transform: translate(-50%);
        z-index: 6;
    }
}

.pop-up-modal {
    &__body {
        display: flex;
        background: #fff4f2;
        border-radius: 10px;
    }

    &__image-wrapper {
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
        width: 70%;
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }

    .modal-content {
        padding: 0 !important;
        border-radius: 10px !important;
    }

    .modal-header {
        display: none !important;
    }

    &__main {
        padding: 10px 20px 15px 25px;
        background: rgba(255, 244, 242, 1);
        text-align: center;
    }

    &__title {
        margin-bottom: 15px;
        font-weight: 600;
        font-size: 30px;
        line-height: 42px;
    }

    &__text {
        font-size: 12px;
        line-height: 17px;
        margin-top: 15px;
    }

    &__primary-button {
        background: #e6007e;
        border: 1px solid #e6007e;
        border-radius: 100px;
        color: #fff;
        display: inline-block;
        font-size: 14px;
        font-weight: 500;
        padding: 8px 20px;
        text-align: center;
        margin-top: 10px;
        margin-bottom: 10px;

        &:hover {
            background: #b80065;
            color: #fff;
        }
    }

    &__close {
        font-size: 0;
        width: 45px;
        height: 45px;
        position: relative;
        background-color: transparent;
        border: none;
        margin-right: -1rem;
        cursor: pointer;

        &::after {
            content: '';
            left: 0;
            top: 0;
            width: 45px;
            height: 45px;
            position: absolute;
            background: url('/nuxt-assets/icons/close.svg') no-repeat 50%;
        }
    }

    @media (max-width: 768px) {
        .modal-body {
            padding: 0 !important;
            height: auto !important;
        }

        &__main {
            padding: 5px 20px 20px 20px;
        }

        &__primary-button {
            margin-bottom: 20px;
        }
    }
}

.ico-open-modal {
    background: url('/nuxt-assets/images/icons/popUp/right-pink-arrow.svg') no-repeat 50%;
    background-size: contain;
    display: inline-block;
    min-height: 24px;
    min-width: 24px;
    margin-left: 10px !important;
}
</style>
